import { EMPTY, Observable, ReplaySubject, catchError, filter, shareReplay, tap } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { DoceboToken } from '@app/interfaces/doceboToken.interface';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DoceboService {
  private _authToken$: ReplaySubject<DoceboToken> = new ReplaySubject(1); // Only emits the most recent subject containing the Docebo Token

  constructor(private http: HttpClient) {}

  /**
   * Set a Docebo Token for Preferred User Name
   * @param preferredUserName - preferred user name (email) from Okta User Claims object
   */
  setAccessToken(preferredUserName: string): Observable<any> {
    return this.http
      .post<{ data: DoceboToken }>(`${environment.api.kepler.docebo.url}/user/authToken`, {
        preferred_username: preferredUserName,
      })
      .pipe(
        shareReplay(),
        filter((response) => {
          console.log(`Docebo Service: Response ${JSON.stringify(response)}`);
          return !!response.data;
        }),
        tap((response) => {
          // Set Docebo Auth Token as Replay Subject. No need to store within local storage or
          // Cookie service
          this._authToken$.next(response.data);
          console.log(`Set Docebo Token found: ${preferredUserName}`);
        }),
        catchError((err) => {
          this.handleError(err);
          return EMPTY;
        })
      );
  }

  /**
   * Get specific information about currently Docebo logged user.
   *
   * @returns Observable Docebo User Session object
   */
  getUserSession(): Observable<any> {
    return this.http.get<any>(`${environment.docebo.url}/manage/v1/user/session`);
  }

  /**
   * Get specific information about currently Docebo logged user.
   * TODO: Add Docebo User Interface
   * @param userId - Docebo user id
   * @returns Observable Docebo User Session object
   */
  getUser(userId: string): Observable<any> {
    return this.http.get<any>(`${environment.docebo.url}/manage/v1/user/${userId}`);
  }

  // TODO: Refactor as part of common method or utility
  private handleError(error: HttpErrorResponse): void {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(`Docebo service returned code ${error.status}, body was: `, {
        cause: error.error,
      });
    }
  }

  /*
   * Return Docebo Token observable from Kepler Auth API
   * @returns Observable containing an Docebo Token interface
   */
  get accessToken$(): Observable<DoceboToken> {
    return this._authToken$.asObservable();
  }

  /**
   * Reset or clear Docebo Token from ReplaySubject on user signout
   * Path must be provided. If you do not provide this, it will not delete
   * any related cookies
   * @returns void
   */
  removeDoceboToken(): void {
    this._authToken$.next(null);
  }
}
